import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Stack,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Button,
  Skeleton,
  Tooltip,
  Popover,
  IconButton,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircleOutline as CheckCircleIcon,
  FiberManualRecord as DotIcon,
  BeachAccessOutlined,
  WeekendOutlined as WeekendIcon,
  TimerOutlined as TimerIcon,
  CalendarMonth as CalendarMonthIcon,
  ViewWeekOutlined as ViewWeekIcon,
  EventBusy,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";
import axios from "axios";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import MaterialTypography from "../../../components/MaterialTypography";
import BASEURL from "../../Utils/Baseurl";
import { useUserDetails } from "../../../context/auth";
import { getAllEmployeesQuery, getAllManagersQuery, getAttendanceQuery, getEmployeeByManagerQuery } from "./Queries";

// Column widths for sticky columns
const EMPLOYEE_COL_WIDTH = 200; // in px
const LEAVE_COL_WIDTH = 75; // in px

// Get Sunday of the week for any given date (week starts on Sunday)
const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const diff = d.getDate() - day;
  return new Date(d.getFullYear(), d.getMonth(), diff, 0, 0, 0, 0);
};

// Returns array of 7 Date objects starting from Sunday to Saturday
const getWeekDates = (refDate) => {
  const start = getStartOfWeek(refDate);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const nextDate = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate() + i,
      0,
      0,
      0,
      0
    );
    dates.push(nextDate);
  }
  return dates;
};

// Previous week (-7 days)
const getPreviousWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - 7);
  return d;
};

// Next week (+7 days)
const getNextWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() + 7);
  return d;
};

// Returns all Date objects for a given month and year
const getMonthDates = (year, month) => {
  const totalDays = new Date(year, month + 1, 0).getDate();
  const dates = [];
  for (let d = 1; d <= totalDays; d++) {
    dates.push(new Date(year, month, d, 0, 0, 0, 0));
  }
  return dates;
};

// Format Date object to YYYY-MM-DD
const formatDateKey = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Format dynamic week range label (e.g., "20 Sep - 26 Sep 2026")
const formatWeekRange = (weekDates) => {
  if (!weekDates || weekDates.length !== 7) return "";
  const start = weekDates[0];
  const end = weekDates[6];
  const startDay = start.getDate();
  const endDay = end.getDate();
  const startMonth = start.toLocaleString("en-US", { month: "short" });
  const endMonth = end.toLocaleString("en-US", { month: "short" });
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  if (startYear === endYear) {
    if (startMonth === endMonth) {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`;
    }
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`;
  }
  return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
};

// Check if two dates represent the exact same calendar day
const isSameDay = (d1, d2) => {
  if (!d1 || !d2) return false;
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

// Calculate duration between startTime and endTime (e.g., "09:40:56" -> "18:36:52" => "8h 55m")
const calculateDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return null;
  const parseTime = (t) => {
    if (typeof t !== "string") return null;
    const parts = t.split(":").map(Number);
    if (parts.some(isNaN)) return null;
    return parts[0] * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
  };
  const startSec = parseTime(startTime);
  const endSec = parseTime(endTime);
  if (startSec === null || endSec === null) return null;
  const diffSec = endSec - startSec;
  if (diffSec <= 0) return null;
  const hours = Math.floor(diffSec / 3600);
  const minutes = Math.floor((diffSec % 3600) / 60);
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours} Hours`;
  return `${minutes}m`;
};

// Format working hours from total string or calculate
const formatWorkingHours = (hoursStr, startTime, endTime) => {
  if (hoursStr && typeof hoursStr === "string" && hoursStr.trim() !== "" && hoursStr !== "0:00") {
    const parts = hoursStr.split(":");
    if (parts.length >= 2) {
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (!isNaN(h)) {
        if (!isNaN(m) && m > 0) {
          return `${h}h ${m}m`;
        }
        return `${h} Hours`;
      }
    }
    const num = parseFloat(hoursStr);
    if (!isNaN(num)) {
      const h = Math.floor(num);
      const m = Math.round((num - h) * 60);
      return m > 0 ? `${h}h ${m}m` : `${h} Hours`;
    }
  }

  const calculated = calculateDuration(startTime, endTime);
  if (calculated) return calculated;

  return "8 Hours";
};

// Month Names for Select
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_SHORT_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Calculate ISO week number
const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

// Returns all 7-day weeks (Sunday-Saturday) that overlap with the given month
const getWeeksForMonth = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const start = getStartOfWeek(firstDay);
  const weeks = [];
  const curr = new Date(start);

  while (curr <= lastDay || weeks.length < 5) {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(new Date(curr.getFullYear(), curr.getMonth(), curr.getDate() + i));
    }
    const weekNum = getWeekNumber(days[3]);
    weeks.push({
      weekNum,
      days,
      startDate: days[0],
      endDate: days[6],
    });
    curr.setDate(curr.getDate() + 7);
    if (curr > lastDay && weeks.length >= 4) {
      break;
    }
  }

  return weeks;
};

const EmployeeAttendance = () => {
  const { AccessData } = useUserDetails();
  const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const loggedInUserId = sessionUser?.id;
  const loggedInEmpId = sessionUser?.empid;

  // View mode: 'weekly' or 'monthly'
  const [viewMode, setViewMode] = useState("weekly");

  // Selected Month and Year for data querying (0-11 for month)
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Reference date for currently displayed 7-day week (in weekly view)
  const [selectedWeekDate, setSelectedWeekDate] = useState(new Date());

  // Filter states
  const [selectedManager, setSelectedManager] = useState("ALL");
  const [selectedEmployee, setSelectedEmployee] = useState("ALL");

  // Data states
  const [loading, setLoading] = useState(true);
  const [managersList, setManagersList] = useState([]);
  const [employeesDropdownList, setEmployeesDropdownList] = useState([]);
  const [attendanceUsers, setAttendanceUsers] = useState([]); // List of employees with attendance & summary
  const [attendanceMap, setAttendanceMap] = useState({}); // { [empid]: { [dateKey]: [events] } }

  // Today reference (midnight)
  const today = useMemo(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate(), 0, 0, 0, 0);
  }, []);

  // Calculate 7-day week dates for weekly view
  const weekDates = useMemo(() => getWeekDates(selectedWeekDate), [selectedWeekDate]);

  // Calculate all days in the selected month for monthly view
  const monthDates = useMemo(
    () => getMonthDates(selectedYear, selectedMonth),
    [selectedYear, selectedMonth]
  );

  // Year options: currentYear +/- 5 years
  const yearOptions = useMemo(() => {
    const currentYr = new Date().getFullYear();
    const list = [];
    for (let y = currentYr - 2; y <= currentYr + 2; y++) {
      list.push(y);
    }
    return list;
  }, []);

  //role permission for manager attendance
  const isManager = AccessData?.some(
    (item) =>
      item.module === "Attendance" &&
      item.component === "ManagerAttendance" &&
      item.can_access
  );

  //role permission for HR attendance
  const isHR = AccessData?.some(
    (item) =>
      item.module === "Attendance" &&
      item.component === "HRAttendance" &&
      item.can_access
  );

  const userId = sessionStorage.getItem("id");

  // FETCH MANAGERS & EMPLOYEES METADATA  
  const fetchMetadata = useCallback(async () => {
    try {
      const currentUserId = userId || loggedInUserId;

      // 1. Fetch All Managers
      const mgrRes = await axios.post(
        BASEURL,
        { query: getAllManagersQuery },
        { headers: { "Content-Type": "application/json" } }
      );

      let rawManagers = [];
      if (mgrRes.data?.data?.allManagers) {
        rawManagers = mgrRes.data.data.allManagers;
        const formattedManagers = rawManagers
          .filter((m) => m?.user)
          .map((m) => ({
            id: m.user.id,
            empid: m.user.empid,
            name: `${m.user.firstName || ""} ${m.user.lastName || ""}`.trim() || "Manager",
            department: m.user.department || "",
          }));
        setManagersList(formattedManagers);
      }

      // 2. Check if current user ID exists in allManagers user list
      const isUserInManagerList = rawManagers.some(
        (m) =>
          String(m?.user?.id) === String(currentUserId) ||
          (m?.user?.empid && String(m?.user?.empid) === String(loggedInEmpId))
      );

      const shouldFilterAsManager = isManager && !isHR && isUserInManagerList;

      if (shouldFilterAsManager) {
        setSelectedManager(currentUserId);
      } else if (isManager && !isHR) {
        setSelectedManager("ALL");
      }

      // 3. Fetch Active Employees
      const employeesGql = shouldFilterAsManager
        ? getEmployeeByManagerQuery
        : getAllEmployeesQuery;

      const empRes = await axios.post(
        BASEURL,
        {
          query: employeesGql,
          ...(shouldFilterAsManager ? { variables: { id: currentUserId } } : {}),
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (empRes.data?.data) {
        const empData = shouldFilterAsManager
          ? empRes.data.data.manager || []
          : (empRes.data.data.activeUsersDetail || []).filter(
              (u) => u.isSuperuser === false && u.isActive !== false
            );

        const formattedEmployees = empData.map((emp) => ({
          id: emp.id,
          empid: emp.empid || emp.id,
          name:
            `${emp.firstName || ""} ${emp.lastName || ""}`.trim() ||
            emp.username ||
            "Employee",
          designation: emp.designation || emp.department || "Staff",
          department: emp.department || "",
          teamleader: emp.teamleader || "",
        }));

        setEmployeesDropdownList(formattedEmployees);
      }
    } catch (err) {
      console.error("Error fetching metadata:", err);
    }
  }, [AccessData, isManager, isHR, userId, loggedInUserId, loggedInEmpId]);

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  // FETCH ATTENDANCE DATA USING getAttendanceQuery

  const fetchMonthlyAttendance = useCallback(async () => {
    try {
      setLoading(true);

      const variables = {
        month: selectedMonth + 1, // 1 to 12
        year: selectedYear,
      };

      // Set empid and teamleader based on filters and logged in user
      if (selectedEmployee && selectedEmployee !== "ALL") {
        variables.empid = selectedEmployee;
      } else if (!isManager && !isHR && loggedInEmpId) {
        variables.empid = loggedInEmpId;
      }

      if (selectedManager && selectedManager !== "ALL") {
        variables.teamleader = selectedManager;
      }

      const response = await axios.post(
        BASEURL,
        {
          query: getAttendanceQuery,
          variables,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const resData = response.data?.data?.allUserMonthlyAttendance;
      if (resData?.user) {
        const rawUsers = Array.isArray(resData.user)
          ? resData.user
          : [resData.user];

        const usersList = rawUsers.map((u) => {

          // Find extra metadata from dropdown list if available
          const meta = employeesDropdownList.find(
            (e) => e.empid === u.empid || e.id === u.empid
          );
          return {
            id: u.empid,
            empid: u.empid,
            name:
              `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
              meta?.name ||
              u.empid,
            designation: meta?.designation || "Staff",
            department: meta?.department || "",
            summary: u.summary || {},
            dates: u.dates || [],
          };
        });

        // Build attendance fast lookup map: { [empid]: { [dateKey]: [events] } }
        const newAttendanceMap = {};
        usersList.forEach((user) => {
          newAttendanceMap[user.empid] = {};
          if (Array.isArray(user.dates)) {
            user.dates.forEach((d) => {
              if (d.date) {
                newAttendanceMap[user.empid][d.date] = d.events || [];
              }
            });
          }
        });

        setAttendanceUsers(usersList);
        setAttendanceMap(newAttendanceMap);
      } else {
        setAttendanceUsers([]);
        setAttendanceMap({});
      }
    } catch (error) {
      console.error("Error fetching attendance data via getAttendanceQuery:", error);
      setAttendanceUsers([]);
      setAttendanceMap({});
    } finally {
      setLoading(false);
    }
  }, [
    selectedMonth,
    selectedYear,
    selectedEmployee,
    selectedManager,
    AccessData,
    loggedInEmpId,
    loggedInUserId,
    employeesDropdownList,
  ]);

  useEffect(() => {
    fetchMonthlyAttendance();
  }, [fetchMonthlyAttendance]);

  // Month change from select
  const handleMonthSelect = (newMonthIdx) => {
    setSelectedMonth(newMonthIdx);
    setSelectedWeekDate(new Date(selectedYear, newMonthIdx, 1));
  };

  // Year change from select
  const handleYearSelect = (newYear) => {
    setSelectedYear(newYear);
    setSelectedWeekDate(new Date(newYear, selectedMonth, 1));
  };

  // Weekly Navigation Handlers with Split-Week Month Transitions
  const handlePrevWeek = () => {
    const currentWeekDates = getWeekDates(selectedWeekDate);
    const startDay = currentWeekDates[0];
    const endDay = currentWeekDates[6];
    const isSplitWeek =
      startDay.getMonth() !== endDay.getMonth() ||
      startDay.getFullYear() !== endDay.getFullYear();

    // If on a boundary week and currently showing the later month's data,
    // stay in the SAME week, switch to the earlier month, and fetch earlier month's data:
    if (isSplitWeek && selectedMonth === endDay.getMonth()) {
      setSelectedMonth(startDay.getMonth());
      setSelectedYear(startDay.getFullYear());
      setSelectedWeekDate(startDay);
      return;
    }

    // Otherwise, move back 7 days
    const prevDate = getPreviousWeek(selectedWeekDate);
    setSelectedWeekDate(prevDate);
    const newWeekDates = getWeekDates(prevDate);
    const newStart = newWeekDates[0];
    const newEnd = newWeekDates[6];
    const newIsSplit =
      newStart.getMonth() !== newEnd.getMonth() ||
      newStart.getFullYear() !== newEnd.getFullYear();

    // When moving backwards into a split week, show the end month (the month we're entering from)
    if (newIsSplit) {
      setSelectedMonth(newEnd.getMonth());
      setSelectedYear(newEnd.getFullYear());
    } else {
      setSelectedMonth(newStart.getMonth());
      setSelectedYear(newStart.getFullYear());
    }
  };

  const handleNextWeek = () => {
    const currentWeekDates = getWeekDates(selectedWeekDate);
    const startDay = currentWeekDates[0];
    const endDay = currentWeekDates[6];
    const isSplitWeek =
      startDay.getMonth() !== endDay.getMonth() ||
      startDay.getFullYear() !== endDay.getFullYear();

    // If on a boundary week and currently showing the earlier month's data,
    // stay in the SAME week, switch to the next month, and fetch next month's data:
    if (isSplitWeek && selectedMonth === startDay.getMonth()) {
      setSelectedMonth(endDay.getMonth());
      setSelectedYear(endDay.getFullYear());
      setSelectedWeekDate(endDay);
      return;
    }

    // Otherwise, advance 7 days
    const nextDate = getNextWeek(selectedWeekDate);
    setSelectedWeekDate(nextDate);
    const newWeekDates = getWeekDates(nextDate);
    const newStart = newWeekDates[0];
    const newEnd = newWeekDates[6];
    const newIsSplit =
      newStart.getMonth() !== newEnd.getMonth() ||
      newStart.getFullYear() !== newEnd.getFullYear();

    // When moving forwards into a split week, show the start month (the month we're entering into)
    if (newIsSplit) {
      setSelectedMonth(newStart.getMonth());
      setSelectedYear(newStart.getFullYear());
    } else {
      setSelectedMonth(newStart.getMonth());
      setSelectedYear(newStart.getFullYear());
    }
  };

  // Monthly Navigation Handlers
  const handlePrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear((prev) => prev - 1);
      setSelectedWeekDate(new Date(selectedYear - 1, 11, 1));
    } else {
      setSelectedMonth((prev) => prev - 1);
      setSelectedWeekDate(new Date(selectedYear, selectedMonth - 1, 1));
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear((prev) => prev + 1);
      setSelectedWeekDate(new Date(selectedYear + 1, 0, 1));
    } else {
      setSelectedMonth((prev) => prev + 1);
      setSelectedWeekDate(new Date(selectedYear, selectedMonth + 1, 1));
    }
  };

  const handleGoToday = () => {
    const now = new Date();
    setSelectedWeekDate(now);
    setSelectedMonth(now.getMonth());
    setSelectedYear(now.getFullYear());
    setPickerMonth(now.getMonth());
    setPickerYear(now.getFullYear());
  };

  // Popover state for Month/Week picker
  const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
  const isPickerOpen = Boolean(pickerAnchorEl);

  // Browsing month & year inside the popover
  const [pickerMonth, setPickerMonth] = useState(selectedMonth);
  const [pickerYear, setPickerYear] = useState(selectedYear);

  const handleOpenPicker = (event) => {
    setPickerAnchorEl(event.currentTarget);
    if (viewMode === "weekly") {
      setPickerMonth(selectedWeekDate.getMonth());
      setPickerYear(selectedWeekDate.getFullYear());
    } else {
      setPickerMonth(selectedMonth);
      setPickerYear(selectedYear);
    }
  };

  const handleClosePicker = () => {
    setPickerAnchorEl(null);
  };

  const handlePickerPrevYear = () => {
    setPickerYear((prev) => prev - 1);
  };

  const handlePickerNextYear = () => {
    setPickerYear((prev) => prev + 1);
  };

  const handlePickerPrevMonth = () => {
    if (pickerMonth === 0) {
      setPickerMonth(11);
      setPickerYear((prev) => prev - 1);
    } else {
      setPickerMonth((prev) => prev - 1);
    }
  };

  const handlePickerNextMonth = () => {
    if (pickerMonth === 11) {
      setPickerMonth(0);
      setPickerYear((prev) => prev + 1);
    } else {
      setPickerMonth((prev) => prev + 1);
    }
  };

  const handleSelectMonthInPicker = (monthIdx) => {
    setSelectedMonth(monthIdx);
    setSelectedYear(pickerYear);
    setSelectedWeekDate(new Date(pickerYear, monthIdx, 1));
    handleClosePicker();
  };

  const handleSelectWeekInPicker = (week) => {
    const middleDate = week.days[3];
    setSelectedWeekDate(middleDate);
    // If middle date is in pickerMonth, use pickerMonth & pickerYear
    if (middleDate.getMonth() === pickerMonth) {
      setSelectedMonth(pickerMonth);
      setSelectedYear(pickerYear);
    } else {
      setSelectedMonth(middleDate.getMonth());
      setSelectedYear(middleDate.getFullYear());
    }
    handleClosePicker();
  };

  const pickerWeeks = useMemo(() => {
    return getWeeksForMonth(pickerYear, pickerMonth);
  }, [pickerYear, pickerMonth]);

  // Filter handlers
  const handleManagerSelect = (mgrId) => {
    setSelectedManager(mgrId);
    setSelectedEmployee("ALL");
  };

  const handleEmployeeSelect = (empId) => {
    setSelectedEmployee(empId);
  };

  // Filtered employees for dropdown
  const filteredEmployeesForDropdown = useMemo(() => {
    if (!isHR || selectedManager === "ALL") {
      return employeesDropdownList;
    }
    return employeesDropdownList.filter(
      (emp) =>
        String(emp.teamleader) === String(selectedManager) ||
        String(emp.managerId) === String(selectedManager)
    );
  }, [employeesDropdownList, selectedManager, isHR]);

  // Displayed employees list in table (derived from attendanceUsers)
  const displayedEmployees = useMemo(() => {
    if (attendanceUsers.length === 0 && employeesDropdownList.length > 0) {
      return employeesDropdownList.filter((emp) => {
        if (selectedEmployee !== "ALL" && emp.empid !== selectedEmployee && emp.id !== selectedEmployee) {
          return false;
        }
        return true;
      });
    }

    return attendanceUsers.filter((emp) => {
      if (
        selectedEmployee !== "ALL" &&
        emp.empid !== selectedEmployee &&
        emp.id !== selectedEmployee
      ) {
        return false;
      }
      return true;
    });
  }, [attendanceUsers, employeesDropdownList, selectedEmployee]);


  // EVENT PILL RENDERER (Supports Multiple Events per Day)  

  const renderEventPill = (event, dateStr, isCurrentDay) => {
    if (!event) return null;

    const eventType = (event.eventType || "").toUpperCase();
    const eventStatus = (event.status || "").toLowerCase();
    const title = event.title || event.eventType || "Event";

    // 1. PRESENT and Today's Check-in
    if (
      eventType === "PRESENT" ||
      eventType === "ATTENDANCE" ||
      eventStatus === "present"
    ) {
      if (isCurrentDay && (!event.endTime || event.endTime === "00:00:00")) {
        return (
          <Tooltip
            key={`active-${dateStr}`}
            title={`Checked-in at ${event.startTime || "Today"}`}
            arrow
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                backgroundColor: "#fffef4",
                color: "#ffbb00",
                border: "1px solid #ffbb00",
                borderRadius: "14px",
                px: 0.9,
                py: 0.25,
                fontSize: "11px",
                fontWeight: 600,
                boxShadow: "0 1px 2px rgba(0, 121, 107, 0.08)",
              }}
            >
              <DotIcon
                sx={{
                  fontSize: 10,
                  color: "#ffbb00",
                  animation: "dotPulse 2s ease-in-out infinite",
                  "@keyframes dotPulse": {
                    "0%": {
                      transform: "scale(1)",
                      opacity: 1,
                    },
                    "50%": {
                      transform: "scale(1.6)",
                      opacity: 0.5,
                    },
                    "100%": {
                      transform: "scale(1)",
                      opacity: 1,
                    },
                  },
                }}
              />
              <span>Active</span>
            </Box>
          </Tooltip>
        );
      }

      return (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.4,
            backgroundColor: "#E6F7ED",
            color: "#0F9D58",
            border: `1px solid #5aa380`,
            borderRadius: "14px",
            px: 0.9,
            py: 0.25,
            fontSize: "11px",
            fontWeight: 600,
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
          }}
        >
          <CheckCircleIcon sx={{ color: "#0F9D58" }} />
          <span>Present</span>
        </Box>
      );
    }

    // 2. PERMISSION
    if (eventType === "PERMISSION" || title.toLowerCase().includes("permission")) {
      const formatPermissionDuration = (duration) => {
        if (!duration || duration === "Permission") return "Permission";

        return duration
          .replace(/\bhours?\b/gi, "hrs")
          .replace(/\bminutes?\b/gi, "mins");
      };
      const permDuration = calculateDuration(event.startTime, event.endTime) || "Permission";
      const formattedDuration = formatPermissionDuration(permDuration);
      return (
        <Tooltip
          key={`perm-${dateStr}-${event.startTime}`}
          title={`Permission: ${event.startTime || ""} - ${event.endTime || ""} (${event.status || "Approved"})`}
          arrow
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              backgroundColor: "#EFF6FF",
              color: "#2563EB",
              border: "1px solid #BFDBFE",
              borderRadius: "14px",
              px: 0.6,
              py: 0.25,
              fontSize: "10px",
              fontWeight: 600,
              boxShadow: "0 1px 2px rgba(37, 99, 235, 0.08)",
            }}
          >
            <TimerIcon sx={{ fontSize: 12, color: "#2563EB" }} />

            <span>
              {formattedDuration === "Permission"
                ? "Permission"
                : `${formattedDuration} Perm`}
            </span>
          </Box>
        </Tooltip>
      );
    }

    // 3. LEAVE
    if (
      eventType.includes("LEAVE") ||
      eventStatus === "leave" ||
      title.toLowerCase().includes("leave")
    ) {
      const isPending = event.status?.toLowerCase() === "pending";

      return (
        <Tooltip
          key={`leave-${dateStr}`}
          title={`${title} (${event.status || "Approved"})`}
          arrow
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.4,
              backgroundColor: "#FEF2F2",
              color: "#DC2626",
              border: isPending
                ? "1px dotted #DC2626"
                : "1px solid #FECACA",
              borderRadius: "14px",
              px: 0.7,
              py: 0.25,
              fontSize: "11px",
              fontWeight: 600,
              boxShadow: "0 1px 2px rgba(220, 38, 38, 0.08)",
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
              }}
            >
              <EventBusy sx={{ fontSize: 12, color: "#DC2626" }} />

              {isPending && (
                <Box
                  sx={{
                    position: "absolute",
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: "#DC2626",
                    top: -1,
                    right: -2,
                    animation: "pendingDot 1.2s ease-in-out infinite",

                    "@keyframes pendingDot": {
                      "0%, 100%": {
                        opacity: 1,
                        transform: "scale(1)",
                      },
                      "50%": {
                        opacity: 0.25,
                        transform: "scale(0.7)",
                      },
                    },
                  }}
                />
              )}
            </Box>

            <span>
              {title.replace(/_/g, " ") || "Leave"}
            </span>
          </Box>
        </Tooltip>
      );
    }

    // 4. HOLIDAY
    if (eventType === "HOLIDAY" || title.toLowerCase().includes("holiday")) {
      return (
        <Tooltip key={`hol-${dateStr}`} title={`Holiday: ${title}`} arrow>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.4,
              backgroundColor: "#F3E8FF",
              color: "#7C3AED",
              border: "1px solid #a67cee",
              borderRadius: "14px",
              px: 0.9,
              py: 0.25,
              fontSize: "11px",
              fontWeight: 600,
              boxShadow: "0 1px 2px rgba(124, 58, 237, 0.08)",
            }}
          >
            <BeachAccessOutlined sx={{ fontSize: 12, color: "#7C3AED" }} />
            <span>{title || "Holiday"}</span>
          </Box>
        </Tooltip>
      );
    }

    // 5. WEEK OFF
    if (eventType.includes("WEEK") || title.toLowerCase().includes("week off")) {
      return (
        <Box
          key={`weekoff-${dateStr}`}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.4,
            backgroundColor: "#F3F4F6",
            color: "#6B7280",
            border: "1px solid #b8b8b8",
            borderRadius: "14px",
            px: 0.7,
            py: 0.25,
            fontSize: "11px",
            fontWeight: 500,
          }}
        >
          <WeekendIcon sx={{ fontSize: 11, color: "#9CA3AF" }} />
          <span>Week Off</span>
        </Box>
      );
    }

    // Fallback Pill
    return (
      <Box
        key={`other-${dateStr}`}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          backgroundColor: "#FCE7F3",
          color: "#DB2777",
          border: "1px solid #FBCFE8",
          borderRadius: "14px",
          px: 0.9,
          py: 0.25,
          fontSize: "11px",
          fontWeight: 500,
          boxShadow: "0 1px 2px rgba(219, 39, 119, 0.08)",
        }}
      >
        <span>{title}</span>
      </Box>
    );
  };

  // Resolve events or default fallback for a given employee and date
  const renderCellContent = (empid, date, isCurrentDay, isOutOfMonth) => {

    // If out of the selected month in weekly view -> Greyed out cell
    if (isOutOfMonth) {
      return null;
    }

    const dateKey = formatDateKey(date);
    const dayEvents = attendanceMap[empid]?.[dateKey];

    // If explicit events exist in the response
    if (Array.isArray(dayEvents) && dayEvents.length > 0) {
      return (
        <Stack direction="column" spacing={0.4} alignItems="center" justifyContent="center">
          {dayEvents.map((evt, idx) => renderEventPill(evt, `${dateKey}-${idx}`, isCurrentDay))}
        </Stack>
      );
    }

    return null;
  };

  // Determine active columns based on view mode
  const activeColumns = viewMode === "weekly" ? weekDates : monthDates;

  return (
    <DashboardLayout>
      <Card
        sx={{
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          border: "1px solid #E2E8F0",
          backgroundColor: "#ffffff",
          p: { xs: 1.5, sm: 2.5 },
        }}
      >
        {/* Card Header with Title and Status Legend */}
        <CardHeader
          sx={{ px: 0, pt: 0, pb: 2 }}
          title={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1.5}
            >
              <Stack direction="column" spacing={0.3}>
                <MaterialTypography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: "#1E293B", letterSpacing: "-0.3px" }}
                >
                  Employee Attendance
                </MaterialTypography>
                <MaterialTypography variant="caption" sx={{ color: "#64748B" }}>
                  {viewMode === "weekly"
                    ? "Weekly schedule view (Sunday to Saturday)"
                    : `Monthly schedule view (${MONTH_NAMES[selectedMonth]} ${selectedYear})`}
                </MaterialTypography>
              </Stack>

              {/* Status Legend Pills */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ display: { xs: "none", lg: "flex" } }}
              >
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.35,
                    borderRadius: "12px",
                    backgroundColor: "#E6F7ED",
                    color: "#0F9D58",
                    fontSize: "11px",
                    fontWeight: 600,
                    border: "1px solid #5aa380",
                  }}
                >
                  ● Present
                </Box>
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.35,
                    borderRadius: "12px",
                    backgroundColor: "#fffef4",
                    color: "#ffbb00",
                    fontSize: "11px",
                    fontWeight: 600,
                    border: "1px solid #ffbb00",
                  }}
                >
                  ● Active
                </Box>
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.35,
                    borderRadius: "12px",
                    backgroundColor: "#EFF6FF",
                    color: "#2563EB",
                    fontSize: "11px",
                    fontWeight: 600,
                    border: "1px solid #2563EB",
                  }}
                >
                  ● Permission
                </Box>
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.35,
                    borderRadius: "12px",
                    backgroundColor: "#FEF2F2",
                    color: "#DC2626",
                    fontSize: "11px",
                    fontWeight: 600,
                    border: "1px solid #DC2626",
                  }}
                >
                  ● Leave
                </Box>
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.35,
                    borderRadius: "12px",
                    backgroundColor: "#F3E8FF",
                    color: "#7C3AED",
                    fontSize: "11px",
                    fontWeight: 600,
                    border: "1px solid #a67cee",
                  }}
                >
                  ● Holiday
                </Box>
              </Stack>
            </Stack>
          }
        />

        <CardContent sx={{ px: 0, pb: 0 }}>

          {/* 1. TOP FILTER ROW (SINGLE HORIZONTAL ROW ON DESKTOP)     */}
          <Box
            sx={{
              p: 2,
              backgroundColor: "#F8FAFC",
              borderRadius: "12px",
              border: "1px solid #EDF2F7",
              mb: 2.5,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              spacing={2}
            >

              {/* RIGHT SIDE: Manager, Employee, & View Filter Toggle */}
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                {/* Manager Dropdown */}
                {(isHR) && (
                  <Box sx={{ minWidth: { xs: 140, sm: 175 } }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#64748B",
                        mb: 0.5,
                      }}
                    >
                      Manager
                    </Typography>

                    <FormControl fullWidth size="small">
                      <Select
                        value={selectedManager}
                        onChange={(e) => handleManagerSelect(e.target.value)}
                        sx={{
                          height: 38,
                          borderRadius: "8px",
                          backgroundColor: "#FFFFFF",
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#334155",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#E2E8F0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#CBD5E1",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#94A3B8",
                            borderWidth: "1px",
                          },
                          "& .MuiSelect-select": {
                            py: "8px",
                            px: "12px",
                          },
                        }}
                      >
                        <MenuItem value="ALL">All Managers</MenuItem>
                        {managersList.map((mgr) => (
                          <MenuItem key={mgr.id} value={mgr.id}>
                            {mgr.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Employee Dropdown */}
                <Box sx={{ minWidth: { xs: 140, sm: 175 } }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#64748B",
                      mb: 0.5,
                    }}
                  >
                    Employee
                  </Typography>

                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedEmployee}
                      onChange={(e) => handleEmployeeSelect(e.target.value)}
                      displayEmpty
                      sx={{
                        height: 38,
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#334155",

                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E2E8F0",
                        },

                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#CBD5E1",
                        },

                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#94A3B8",
                          borderWidth: "1px",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: "8px",
                            border: "1px solid #E2E8F0",
                            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",

                            // Scroll when there are many employees
                            maxHeight: 280,
                            overflowY: "auto",

                            // Custom scrollbar
                            "&::-webkit-scrollbar": {
                              width: "6px",
                            },

                            "&::-webkit-scrollbar-track": {
                              background: "transparent",
                            },

                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#CBD5E1",
                              borderRadius: "10px",
                            },

                            "&::-webkit-scrollbar-thumb:hover": {
                              backgroundColor: "#94A3B8",
                            },

                            "& .MuiMenuItem-root": {
                              fontSize: "14px",
                              fontWeight: 500,
                              minHeight: 38,
                              padding: "6px 10px",
                              borderRadius: "6px",
                              margin: "1px 2px",

                              "&:hover": {
                                backgroundColor: "#F8FAFC",
                              },

                              "&.Mui-selected": {
                                backgroundColor: "#F1F5F9",
                              },

                              "&.Mui-selected:hover": {
                                backgroundColor: "#E2E8F0",
                              },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="ALL">All Employees</MenuItem>

                      {filteredEmployeesForDropdown.map((emp) => (
                        <MenuItem
                          key={emp.id || emp.empid}
                          value={emp.empid || emp.id}
                        >
                          {emp.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Filter Change Button: Weekly vs Monthly Toggle */}
                <Box sx={{ pt: { xs: 1, sm: 2.6 } }}>
                  <Button
                    variant="gradient"
                    startIcon={
                      viewMode === "weekly" ? (
                        <CalendarMonthIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <ViewWeekIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    onClick={() =>
                      setViewMode((prev) => (prev === "weekly" ? "monthly" : "weekly"))
                    }
                    sx={{
                      height: 38,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "13px",
                      backgroundColor: "#0F172A",
                      color: "#ffffff",
                      borderRadius: "8px",
                      px: 2,
                      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.15)",
                      "&:hover": {
                        backgroundColor: "#1E293B",
                      },
                    }}
                  >
                    {viewMode === "weekly" ? "Monthly View" : "Weekly View"}
                  </Button>
                </Box>
              </Stack>
            </Stack>
          </Box>

          {/* 2. NAVIGATION ROW (WEEK OR MONTH CONTROLS)  */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2, px: 0.5 }}
          >
            {/* Previous Navigation Button */}
            <Button
              variant="gradient"
              startIcon={<ChevronLeftIcon />}
              onClick={viewMode === "weekly" ? handlePrevWeek : handlePrevMonth}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                color: "#334155",
                border: "1px solid #aaa",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                px: { xs: 1.5, sm: 2 },
                py: 0.7,
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                "&:hover": {
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #5a5a5a",
                },
              }}
            >
              {viewMode === "weekly" ? "Previous Week" : "Previous Month"}
            </Button>

            {/* Center Dynamic Date Picker Button & Quick Reset Button */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
  variant="gradient"
  onClick={handleOpenPicker}
  startIcon={
    <CalendarMonthIcon sx={{ color: "#2563EB", fontSize: 18 }} />
  }
  endIcon={
    <ArrowDropDownIcon sx={{ color: "#64748B", fontSize: 20 }} />
  }
  sx={{
    textTransform: "none",
    fontWeight: 700,
    color: "#0F172A",
    fontSize: { xs: "13.5px", sm: "15px" },
    letterSpacing: "-0.2px",

    // Recreate outlined appearance
    border: `1px solid ${isPickerOpen ? "#2563EB" : "#CBD5E1"}`,
    backgroundColor: isPickerOpen ? "#F0F7FF" : "#FFFFFF",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",

    borderRadius: "8px",
    px: { xs: 1.5, sm: 2 },
    py: 0.6,
    transition: "all 0.15s ease",

    "&:hover": {
      backgroundColor: "#F8FAFC",
      border: "1px solid #94A3B8",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    },

    "&:focus": {
      border: "1px solid #2563EB",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    },
  }}
>
  {viewMode === "weekly"
    ? formatWeekRange(weekDates)
    : `${MONTH_NAMES[selectedMonth]} ${selectedYear}`}
</Button>

              <Button
                variant="gradient"
                size="small"
                onClick={handleGoToday}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "#2563EB",
                  backgroundColor: "#EFF6FF",
                  borderRadius: "6px",
                  px: 1.2,
                  py: 0.5,
                  "&:hover": {
                    backgroundColor: "#DBEAFE",
                  },
                }}
              >
                {viewMode === "weekly" ? "Today" : "This Month"}
              </Button>
            </Stack>

            {/* Next Navigation Button */}
            <Button
              variant="gradient"
              endIcon={<ChevronRightIcon />}
              onClick={viewMode === "weekly" ? handleNextWeek : handleNextMonth}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                color: "#334155",
                border: "1px solid #aaa",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                px: { xs: 1.5, sm: 2 },
                py: 0.7,
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                "&:hover": {
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #5a5a5a",
                },
              }}
            >
              {viewMode === "weekly" ? "Next Week" : "Next Month"}
            </Button>
          </Stack>

          {/* Date Picker Popover (Month Picker or Week Picker) */}
          <Popover
            open={isPickerOpen}
            anchorEl={pickerAnchorEl}
            onClose={handleClosePicker}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 2,
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                boxShadow:
                  "0 10px 25px -5px rgba(15, 23, 42, 0.12), 0 8px 10px -6px rgba(15, 23, 42, 0.08)",
                width: viewMode === "weekly" ? 360 : 320,
              },
            }}
          >
            {viewMode === "monthly" ? (
              /* MONTH PICKER (Image 1 style) */
              <Box>
                {/* Year Navigation Header */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <IconButton
                    size="small"
                    onClick={handlePickerPrevYear}
                    sx={{
                      color: "#64748B",
                      "&:hover": { backgroundColor: "#F1F5F9" },
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>

                  {/* Year Select Dropdown */}
                  <FormControl size="small" variant="standard">
                    <Select
                      value={pickerYear}
                      onChange={(e) => setPickerYear(Number(e.target.value))}
                      disableUnderline
                      sx={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#0F172A",
                        cursor: "pointer",
                        "& .MuiSelect-select": {
                          py: 0.5,
                          px: 1,
                        },
                      }}
                    >
                      {yearOptions.map((yr) => (
                        <MenuItem key={yr} value={yr}>
                          {yr}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <IconButton
                    size="small"
                    onClick={handlePickerNextYear}
                    sx={{
                      color: "#64748B",
                      "&:hover": { backgroundColor: "#F1F5F9" },
                    }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Stack>

                {/* 12 Months Grid */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 1,
                  }}
                >
                  {MONTH_SHORT_NAMES.map((name, idx) => {
                    const isSelected =
                      selectedMonth === idx && selectedYear === pickerYear;
                    const isCurrentMonth =
                      today.getMonth() === idx &&
                      today.getFullYear() === pickerYear;

                    return (
                      <Button
                        key={name}
                        onClick={() => handleSelectMonthInPicker(idx)}
                        sx={{
                          py: 1.2,
                          borderRadius: "8px",
                          fontSize: "13px",
                          fontWeight: isSelected ? 700 : 500,
                          textTransform: "none",
                          backgroundColor: isSelected
                            ? "#2563EB"
                            : isCurrentMonth
                              ? "#EFF6FF"
                              : "transparent",
                          color: isSelected
                            ? "#FFFFFF"
                            : isCurrentMonth
                              ? "#2563EB"
                              : "#334155",
                          border: isSelected
                            ? "none"
                            : isCurrentMonth
                              ? "1px solid #BFDBFE"
                              : "1px solid transparent",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? "#1D4ED8"
                              : "#F1F5F9",
                          },
                        }}
                      >
                        {name}
                      </Button>
                    );
                  })}
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    pt: 1.5,
                    mt: 1.5,
                    borderTop: "1px solid #F1F5F9",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "11px", color: "#64748B", fontWeight: 500 }}
                  >
                    Selected: {MONTH_NAMES[selectedMonth]} {selectedYear}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      const now = new Date();
                      setSelectedMonth(now.getMonth());
                      setSelectedYear(now.getFullYear());
                      setSelectedWeekDate(now);
                      handleClosePicker();
                    }}
                    sx={{
                      fontSize: "11px",
                      textTransform: "none",
                      py: 0.2,
                      px: 1,
                      minWidth: 0,
                      fontWeight: 600,
                      color: "#2563EB",
                    }}
                  >
                    Current Month
                  </Button>
                </Box>
              </Box>
            ) : (
              /* WEEK PICKER (Image 2 style) */
              <Box>
                {/* Month & Year Navigation Header */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <IconButton
                    size="small"
                    onClick={handlePickerPrevMonth}
                    sx={{
                      color: "#64748B",
                      "&:hover": { backgroundColor: "#F1F5F9" },
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>

                  <Stack direction="row" spacing={1} alignItems="center">
                    {/* Month Selector */}
                    <FormControl size="small" variant="standard">
                      <Select
                        value={pickerMonth}
                        onChange={(e) =>
                          setPickerMonth(Number(e.target.value))
                        }
                        disableUnderline
                        sx={{
                          fontSize: "14px",
                          fontWeight: 700,
                          color: "#0F172A",
                          cursor: "pointer",
                          "& .MuiSelect-select": {
                            py: 0.5,
                            px: 0.8,
                          },
                        }}
                      >
                        {MONTH_NAMES.map((name, idx) => (
                          <MenuItem key={idx} value={idx}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Year Selector */}
                    <FormControl size="small" variant="standard">
                      <Select
                        value={pickerYear}
                        onChange={(e) =>
                          setPickerYear(Number(e.target.value))
                        }
                        disableUnderline
                        sx={{
                          fontSize: "14px",
                          fontWeight: 700,
                          color: "#0F172A",
                          cursor: "pointer",
                          "& .MuiSelect-select": {
                            py: 0.5,
                            px: 0.8,
                          },
                        }}
                      >
                        {yearOptions.map((yr) => (
                          <MenuItem key={yr} value={yr}>
                            {yr}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  <IconButton
                    size="small"
                    onClick={handlePickerNextMonth}
                    sx={{
                      color: "#64748B",
                      "&:hover": { backgroundColor: "#F1F5F9" },
                    }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Stack>

                {/* Weekday Labels Header */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "30px repeat(7, 1fr)",
                    alignItems: "center",
                    pb: 0.8,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#94A3B8",
                      textAlign: "center",
                    }}
                  >
                    Wk
                  </Typography>
                  {WEEKDAY_SHORT.map((dayName) => (
                    <Typography
                      key={dayName}
                      sx={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#64748B",
                        textAlign: "center",
                      }}
                    >
                      {dayName}
                    </Typography>
                  ))}
                </Box>

                {/* Weeks Rows */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, mt: 0.8 }}>
                  {pickerWeeks.map((week, wIdx) => {
                    const isSelectedWeek = isSameDay(
                      weekDates[0],
                      week.days[0]
                    );

                    return (
                      <Box
                        key={`wk-row-${wIdx}`}
                        onClick={() => handleSelectWeekInPicker(week)}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "30px repeat(7, 1fr)",
                          alignItems: "center",
                          py: 0.5,
                          px: 0.2,
                          borderRadius: "8px",
                          cursor: "pointer",
                          backgroundColor: isSelectedWeek
                            ? "#2563EB"
                            : "transparent",
                          transition: "background-color 0.15s ease",
                          "&:hover": {
                            backgroundColor: isSelectedWeek
                              ? "#1D4ED8"
                              : "#F1F5F9",
                          },
                        }}
                      >
                        {/* Week number */}
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: isSelectedWeek ? "#BFDBFE" : "#94A3B8",
                            textAlign: "center",
                            userSelect: "none",
                          }}
                        >
                          {week.weekNum}
                        </Typography>

                        {/* 7 Days in Week */}
                        {week.days.map((d, dIdx) => {
                          const isOutOfMonth = d.getMonth() !== pickerMonth;
                          const isTodayDate = isSameDay(d, today);

                          return (
                            <Box
                              key={`d-${dIdx}`}
                              sx={{
                                textAlign: "center",
                                py: 0.4,
                                position: "relative",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: isSelectedWeek
                                    ? 700
                                    : isTodayDate
                                      ? 700
                                      : 500,
                                  color: isSelectedWeek
                                    ? "#FFFFFF"
                                    : isOutOfMonth
                                      ? "#CBD5E1"
                                      : isTodayDate
                                        ? "#2563EB"
                                        : "#1E293B",
                                  lineHeight: 1.2,
                                }}
                              >
                                {d.getDate()}
                              </Typography>
                              {!isSelectedWeek && isTodayDate && (
                                <Box
                                  sx={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: "50%",
                                    backgroundColor: "#2563EB",
                                    mx: "auto",
                                    mt: 0.2,
                                  }}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })}
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    pt: 1.5,
                    mt: 1,
                    borderTop: "1px solid #F1F5F9",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "11px", color: "#64748B", fontWeight: 500 }}
                  >
                    Selected: {formatWeekRange(weekDates)}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      const now = new Date();
                      setSelectedWeekDate(now);
                      setSelectedMonth(now.getMonth());
                      setSelectedYear(now.getFullYear());
                      handleClosePicker();
                    }}
                    sx={{
                      fontSize: "11px",
                      textTransform: "none",
                      py: 0.2,
                      px: 1,
                      minWidth: 0,
                      fontWeight: 600,
                      color: "#2563EB",
                    }}
                  >
                    Current Week
                  </Button>
                </Box>
              </Box>
            )}
          </Popover>

          {/* 3. ATTENDANCE TABLE STRUCTURE                            */}
          {/* STICKY COLUMNS: Employee (left:0), Leave (left: 240px)   */}
          {/* EXACT MATCHED SEQUENCING FOR HEADER & BODY CELLS         */}
          <TableContainer
            sx={{
              maxHeight: "640px",
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              overflowX: "auto",
              overflowY: "auto",
              backgroundColor: "#ffffff",
              position: "relative",
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#CBD5E1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#94A3B8",
              },
            }}
          >
            <Table
              sx={{
                borderCollapse: "separate",
                borderSpacing: 0,
                width: "max-content",
                minWidth: "100%",
              }}
            >
              <TableHead>
                <TableRow>
                  {/* Column 0: Sticky Employee Header */}
                  <TableCell
                    sx={{
                      width: `${EMPLOYEE_COL_WIDTH}px`,
                      minWidth: `${EMPLOYEE_COL_WIDTH}px`,
                      maxWidth: `${EMPLOYEE_COL_WIDTH}px`,
                      position: "sticky",
                      left: 0,
                      top: 0,
                      zIndex: 30,
                      backgroundColor: "#F8FAFC",
                      fontWeight: 700,
                      fontSize: "13px",
                      color: "#334155",
                      borderRight: viewMode === "weekly" ? "1px solid #E2E8F0" : "2px solid #CBD5E1",
                      borderBottom: "2px solid #CBD5E1",
                      boxShadow: viewMode === "monthly" ? "4px 0 8px -2px rgba(0, 0, 0, 0.06)" : "none",
                      py: 1.5,
                      px: 2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Employee
                  </TableCell>

                  {/* WEEKLY VIEW: Sticky Leave Count Header */}
                  {viewMode === "weekly" && (
                    <TableCell
                      align="center"
                      sx={{
                        width: `${LEAVE_COL_WIDTH}px`,
                        minWidth: `${LEAVE_COL_WIDTH}px`,
                        maxWidth: `${LEAVE_COL_WIDTH}px`,
                        position: "sticky",
                        left: `${EMPLOYEE_COL_WIDTH}px`,
                        top: 0,
                        zIndex: 30,
                        backgroundColor: "#F8FAFC",
                        fontWeight: 700,
                        fontSize: "13px",
                        color: "#334155",
                        borderRight: "2px solid #CBD5E1",
                        borderBottom: "2px solid #CBD5E1",
                        boxShadow: "4px 0 8px -2px rgba(0, 0, 0, 0.06)",
                        py: 1.5,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Leave
                    </TableCell>
                  )}

                  {/* MONTHLY VIEW: Summary Headers (Total Days, Present, Leave, LOP, Week Off, Holiday) */}
                  {viewMode === "monthly" && (
                    <>
                      <TableCell
                        align="center"
                        sx={{
                          width: "80px",
                          minWidth: "80px",
                          maxWidth: "80px",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backgroundColor: "#F8FAFC",
                          fontWeight: 700,
                          fontSize: "12px",
                          color: "#334155",
                          borderRight: "1px solid #E2E8F0",
                          borderBottom: "2px solid #CBD5E1",
                          py: 1.5,
                          px: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Total Days
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          width: "75px",
                          minWidth: "75px",
                          maxWidth: "75px",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backgroundColor: "#F8FAFC",
                          fontWeight: 700,
                          fontSize: "12px",
                          color: "#334155",
                          borderRight: "1px solid #E2E8F0",
                          borderBottom: "2px solid #CBD5E1",
                          py: 1.5,
                          px: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Present
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          width: "75px",
                          minWidth: "75px",
                          maxWidth: "75px",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backgroundColor: "#F8FAFC",
                          fontWeight: 700,
                          fontSize: "12px",
                          color: "#334155",
                          borderRight: "1px solid #E2E8F0",
                          borderBottom: "2px solid #CBD5E1",
                          py: 1.5,
                          px: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Leave
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          width: "70px",
                          minWidth: "70px",
                          maxWidth: "70px",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backgroundColor: "#F8FAFC",
                          fontWeight: 700,
                          fontSize: "12px",
                          color: "#334155",
                          borderRight: "1px solid #E2E8F0",
                          borderBottom: "2px solid #CBD5E1",
                          py: 1.5,
                          px: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        LOP
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          width: "75px",
                          minWidth: "75px",
                          maxWidth: "75px",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backgroundColor: "#F8FAFC",
                          fontWeight: 700,
                          fontSize: "12px",
                          color: "#334155",
                          borderRight: "1px solid #E2E8F0",
                          borderBottom: "2px solid #CBD5E1",
                          py: 1.5,
                          px: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Week Off
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          width: "75px",
                          minWidth: "75px",
                          maxWidth: "75px",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backgroundColor: "#F8FAFC",
                          fontWeight: 700,
                          fontSize: "12px",
                          color: "#334155",
                          borderRight: "2px solid #CBD5E1",
                          borderBottom: "2px solid #CBD5E1",
                          py: 1.5,
                          px: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Holiday
                      </TableCell>
                    </>
                  )}

                  {/* Dynamic Date Headers */}
                  {activeColumns.map((date, idx) => {
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    const isOutOfMonth =
                      viewMode === "weekly" &&
                      (date.getMonth() !== selectedMonth || date.getFullYear() !== selectedYear);

                    const colWidth = viewMode === "monthly" ? "90px" : "130px";

                    if (viewMode === "weekly") {
                      const weekdayName = WEEKDAY_NAMES[date.getDay()];
                      return (
                        <TableCell
                          key={`weekly-hdr-${date.getTime()}-${idx}`}
                          align="center"
                          sx={{
                            width: colWidth,
                            minWidth: colWidth,
                            maxWidth: colWidth,
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                            backgroundColor: isOutOfMonth
                              ? "#F1F5F9"
                              : isWeekend
                                ? "#F8FAFC"
                                : "#ffffff",
                            fontWeight: 700,
                            fontSize: "13px",
                            color: isOutOfMonth ? "#94A3B8" : isWeekend ? "#767c85" : "#1E293B",
                            borderRight: idx === activeColumns.length - 1 ? "none" : "1px solid #E2E8F0",
                            borderBottom: "2px solid #CBD5E1",
                            py: 1.5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {weekdayName}
                        </TableCell>
                      );
                    } else {
                      const dayNum = String(date.getDate()).padStart(2, "0");
                      const weekdayShort = WEEKDAY_SHORT[date.getDay()];
                      return (
                        <TableCell
                          key={`monthly-hdr-${date.getTime()}-${idx}`}
                          align="center"
                          sx={{
                            width: colWidth,
                            minWidth: colWidth,
                            maxWidth: colWidth,
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                            backgroundColor: isWeekend ? "#e2e2e2" : "#ffffff",
                            borderRight: idx === activeColumns.length - 1 ? "none" : "1px solid #E2E8F0",
                            borderBottom: "2px solid #CBD5E1",
                            py: 1,
                            px: 0.5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "13px",
                              color: isWeekend ? "#444649" : "#1E293B",
                              lineHeight: 1.2,
                            }}
                          >
                            {dayNum}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: isWeekend ? "#66707e" : "#64748B",
                              lineHeight: 1,
                            }}
                          >
                            {weekdayShort}
                          </Typography>
                        </TableCell>
                      );
                    }
                  })}
                </TableRow>
                <TableBody>
                  {/* Skeleton Loading State */}
                  {loading && (
                    <>
                      {[1, 2, 3, 4, 5].map((rowIdx) => (
                        <TableRow key={`skeleton-${rowIdx}`}>
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              zIndex: 20,
                              backgroundColor: "#ffffff",
                              width: `${EMPLOYEE_COL_WIDTH}px`,
                              minWidth: `${EMPLOYEE_COL_WIDTH}px`,
                              maxWidth: `${EMPLOYEE_COL_WIDTH}px`,
                              borderRight: viewMode === "weekly" ? "1px solid #E2E8F0" : "2px solid #CBD5E1",
                              borderBottom: "1px solid #F1F5F9",
                              py: 1.5,
                              px: 2,
                            }}
                          >
                            <Skeleton variant="text" width={130} height={20} />
                            <Skeleton variant="text" width={90} height={15} />
                          </TableCell>
                          {viewMode === "weekly" ? (
                            <TableCell
                              align="center"
                              sx={{
                                position: "sticky",
                                left: `${EMPLOYEE_COL_WIDTH}px`,
                                zIndex: 20,
                                backgroundColor: "#ffffff",
                                width: `${LEAVE_COL_WIDTH}px`,
                                minWidth: `${LEAVE_COL_WIDTH}px`,
                                maxWidth: `${LEAVE_COL_WIDTH}px`,
                                borderRight: "2px solid #CBD5E1",
                                borderBottom: "1px solid #F1F5F9",
                              }}
                            >
                              <Skeleton variant="circular" width={24} height={24} sx={{ mx: "auto" }} />
                            </TableCell>
                          ) : (
                            <>
                              <TableCell align="center" sx={{ width: "80px", borderRight: "1px solid #F1F5F9", borderBottom: "1px solid #F1F5F9" }}>
                                <Skeleton variant="rounded" width={32} height={22} sx={{ mx: "auto", borderRadius: "10px" }} />
                              </TableCell>
                              <TableCell align="center" sx={{ width: "75px", borderRight: "1px solid #F1F5F9", borderBottom: "1px solid #F1F5F9" }}>
                                <Skeleton variant="rounded" width={32} height={22} sx={{ mx: "auto", borderRadius: "10px" }} />
                              </TableCell>
                              <TableCell align="center" sx={{ width: "75px", borderRight: "1px solid #F1F5F9", borderBottom: "1px solid #F1F5F9" }}>
                                <Skeleton variant="rounded" width={32} height={22} sx={{ mx: "auto", borderRadius: "10px" }} />
                              </TableCell>
                              <TableCell align="center" sx={{ width: "70px", borderRight: "1px solid #F1F5F9", borderBottom: "1px solid #F1F5F9" }}>
                                <Skeleton variant="rounded" width={32} height={22} sx={{ mx: "auto", borderRadius: "10px" }} />
                              </TableCell>
                              <TableCell align="center" sx={{ width: "75px", borderRight: "1px solid #F1F5F9", borderBottom: "1px solid #F1F5F9" }}>
                                <Skeleton variant="rounded" width={32} height={22} sx={{ mx: "auto", borderRadius: "10px" }} />
                              </TableCell>
                              <TableCell align="center" sx={{ width: "75px", borderRight: "2px solid #CBD5E1", borderBottom: "1px solid #F1F5F9" }}>
                                <Skeleton variant="rounded" width={32} height={22} sx={{ mx: "auto", borderRadius: "10px" }} />
                              </TableCell>
                            </>
                          )}
                          {activeColumns.map((_, colIdx) => (
                            <TableCell
                              key={colIdx}
                              align="center"
                              sx={{
                                borderRight: colIdx === activeColumns.length - 1 ? "none" : "1px solid #F1F5F9",
                                borderBottom: "1px solid #F1F5F9",
                                height: 74,
                                width: viewMode === "monthly" ? "90px" : "130px",
                                minWidth: viewMode === "monthly" ? "90px" : "130px",
                              }}
                            >
                              <Skeleton
                                variant="rounded"
                                width={65}
                                height={26}
                                sx={{ mx: "auto", borderRadius: "14px" }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </>
                  )}

                  {/* Empty State */}
                  {!loading && displayedEmployees.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={viewMode === "weekly" ? activeColumns.length + 2 : activeColumns.length + 7}
                        align="center"
                        sx={{
                          py: 8,
                          borderBottom: "none",
                          backgroundColor: "#ffffff",
                        }}
                      >
                        <Stack direction="column" spacing={1} alignItems="center" justifyContent="center">
                          <Typography variant="body1" fontWeight={600} color="#64748B">
                            No attendance records found
                          </Typography>
                          <Typography variant="caption" color="#94A3B8">
                            No employee matched the current filter criteria for {MONTH_NAMES[selectedMonth]} {selectedYear}.
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Data Rows */}
                  {!loading &&
                    displayedEmployees.map((emp) => {
                      const leaveCount =
                        viewMode === "weekly"
                          ? weekDates.reduce((count, date) => {
                            const dateKey = formatDateKey(date);
                            const events = attendanceMap[emp.empid]?.[dateKey] || [];
                            const isLeave = events.some(
                              (e) =>
                                (e.eventType || "").toUpperCase().includes("LEAVE") ||
                                (e.status || "").toLowerCase() === "leave" ||
                                (e.title || "").toLowerCase().includes("leave")
                            );
                            return count + (isLeave ? 1 : 0);
                          }, 0)
                          : emp.summary?.totalLeave || 0;

                      const summary = emp.summary || {};
                      const totalDays = summary.totalDays ?? 0;
                      const totalPresentDays = summary.totalPresentDays ?? 0;
                      const totalLeave = summary.totalLeave ?? 0;
                      const lopDays = summary.lopDays ?? 0;
                      const totalWeekoff = summary.totalWeekoff ?? 0;
                      const totalHolidays = summary.totalHolidays ?? 0;

                      return (
                        <TableRow
                          key={emp.empid || emp.id}
                          sx={{
                            transition: "background-color 0.15s ease",
                            "&:hover": {
                              backgroundColor: "#F8FAFC",
                              "& .sticky-employee-cell": {
                                backgroundColor: "#F8FAFC",
                              },
                              "& .sticky-leave-cell": {
                                backgroundColor: "#F8FAFC",
                              },
                            },
                          }}
                        >
                          {/* Column 0: Sticky Employee Name & Designation */}
                          <TableCell
                            className="sticky-employee-cell"
                            sx={{
                              position: "sticky",
                              left: 0,
                              zIndex: 20,
                              backgroundColor: "#ffffff",
                              width: `${EMPLOYEE_COL_WIDTH}px`,
                              minWidth: `${EMPLOYEE_COL_WIDTH}px`,
                              maxWidth: `${EMPLOYEE_COL_WIDTH}px`,
                              borderRight: viewMode === "weekly" ? "1px solid #E2E8F0" : "2px solid #CBD5E1",
                              borderBottom: "1px solid #E2E8F0",
                              boxShadow: viewMode === "monthly" ? "4px 0 8px -2px rgba(0, 0, 0, 0.06)" : "none",
                              py: 1.5,
                              px: 2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "13.5px",
                                color: "#0F172A",
                                lineHeight: 1.3,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {emp.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#64748B",
                                mt: 0.3,
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {emp.designation || "Staff"}
                            </Typography>
                          </TableCell>

                          {/* WEEKLY VIEW: Sticky Leave Count */}
                          {viewMode === "weekly" && (
                            <TableCell
                              className="sticky-leave-cell"
                              align="center"
                              sx={{
                                position: "sticky",
                                left: `${EMPLOYEE_COL_WIDTH}px`,
                                zIndex: 20,
                                backgroundColor: "#ffffff",
                                width: `${LEAVE_COL_WIDTH}px`,
                                minWidth: `${LEAVE_COL_WIDTH}px`,
                                maxWidth: `${LEAVE_COL_WIDTH}px`,
                                borderRight: "2px solid #CBD5E1",
                                borderBottom: "1px solid #E2E8F0",
                                boxShadow: "4px 0 8px -2px rgba(0, 0, 0, 0.06)",
                                py: 1.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "12.5px",
                                  color: leaveCount > 0 ? "#7C3AED" : "#64748B",
                                  backgroundColor: leaveCount > 0 ? "#F3E8FF" : "#F1F5F9",
                                  borderRadius: "10px",
                                  display: "inline-block",
                                  px: 1.2,
                                  py: 0.3,
                                  minWidth: 26,
                                }}
                              >
                                {leaveCount}
                              </Typography>
                            </TableCell>
                          )}

                          {/* MONTHLY VIEW: Summary Columns */}
                          {viewMode === "monthly" && (
                            <>
                              {/* Total Days */}
                              <TableCell
                                align="center"
                                sx={{
                                  width: "80px",
                                  minWidth: "80px",
                                  maxWidth: "80px",
                                  borderRight: "1px solid #E2E8F0",
                                  borderBottom: "1px solid #E2E8F0",
                                  py: 1.5,
                                  px: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: "#334155",
                                    backgroundColor: "#F1F5F9",
                                    borderRadius: "10px",
                                    display: "inline-block",
                                    px: 1.2,
                                    py: 0.3,
                                    minWidth: 26,
                                  }}
                                >
                                  {totalDays}
                                </Typography>
                              </TableCell>

                              {/* Present */}
                              <TableCell
                                align="center"
                                sx={{
                                  width: "75px",
                                  minWidth: "75px",
                                  maxWidth: "75px",
                                  borderRight: "1px solid #E2E8F0",
                                  borderBottom: "1px solid #E2E8F0",
                                  py: 1.5,
                                  px: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: totalPresentDays > 0 ? "#0F9D58" : "#64748B",
                                    backgroundColor: totalPresentDays > 0 ? "#E6F7ED" : "#F1F5F9",
                                    borderRadius: "10px",
                                    display: "inline-block",
                                    px: 1.2,
                                    py: 0.3,
                                    minWidth: 26,
                                  }}
                                >
                                  {totalPresentDays}
                                </Typography>
                              </TableCell>

                              {/* Leave */}
                              <TableCell
                                align="center"
                                sx={{
                                  width: "75px",
                                  minWidth: "75px",
                                  maxWidth: "75px",
                                  borderRight: "1px solid #E2E8F0",
                                  borderBottom: "1px solid #E2E8F0",
                                  py: 1.5,
                                  px: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: totalLeave > 0 ? "#DC2626" : "#64748B",
                                    backgroundColor: totalLeave > 0 ? "#FEF2F2" : "#F1F5F9",
                                    borderRadius: "10px",
                                    display: "inline-block",
                                    px: 1.2,
                                    py: 0.3,
                                    minWidth: 26,
                                  }}
                                >
                                  {totalLeave}
                                </Typography>
                              </TableCell>

                              {/* LOP */}
                              <TableCell
                                align="center"
                                sx={{
                                  width: "70px",
                                  minWidth: "70px",
                                  maxWidth: "70px",
                                  borderRight: "1px solid #E2E8F0",
                                  borderBottom: "1px solid #E2E8F0",
                                  py: 1.5,
                                  px: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: lopDays > 0 ? "#E11D48" : "#64748B",
                                    backgroundColor: lopDays > 0 ? "#FFE4E6" : "#F1F5F9",
                                    borderRadius: "10px",
                                    display: "inline-block",
                                    px: 1.2,
                                    py: 0.3,
                                    minWidth: 26,
                                  }}
                                >
                                  {lopDays}
                                </Typography>
                              </TableCell>

                              {/* Week Off */}
                              <TableCell
                                align="center"
                                sx={{
                                  width: "75px",
                                  minWidth: "75px",
                                  maxWidth: "75px",
                                  borderRight: "1px solid #E2E8F0",
                                  borderBottom: "1px solid #E2E8F0",
                                  py: 1.5,
                                  px: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: totalWeekoff > 0 ? "#6B7280" : "#64748B",
                                    backgroundColor: totalWeekoff > 0 ? "#F3E8FF" : "#F1F5F9",
                                    borderRadius: "10px",
                                    display: "inline-block",
                                    px: 1.2,
                                    py: 0.3,
                                    minWidth: 26,
                                  }}
                                >
                                  {totalWeekoff}
                                </Typography>
                              </TableCell>

                              {/* Holiday */}
                              <TableCell
                                align="center"
                                sx={{
                                  width: "75px",
                                  minWidth: "75px",
                                  maxWidth: "75px",
                                  borderRight: "2px solid #CBD5E1",
                                  borderBottom: "1px solid #E2E8F0",
                                  py: 1.5,
                                  px: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: totalHolidays > 0 ? "#7C3AED" : "#64748B",
                                    backgroundColor: totalHolidays > 0 ? "#F3E8FF" : "#F1F5F9",
                                    borderRadius: "10px",
                                    display: "inline-block",
                                    px: 1.2,
                                    py: 0.3,
                                    minWidth: 26,
                                  }}
                                >
                                  {totalHolidays}
                                </Typography>
                              </TableCell>
                            </>
                          )}

                          {/* Date Cells */}
                          {activeColumns.map((date, colIdx) => {
                            const dateKey = formatDateKey(date);
                            const dateNumber = date.getDate();
                            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                            const isCurrentDay = isSameDay(date, new Date());

                            // In Weekly view: check if date is outside the currently selected month
                            const isOutOfMonth =
                              viewMode === "weekly" &&
                              (date.getMonth() !== selectedMonth || date.getFullYear() !== selectedYear);

                            const colWidth = viewMode === "monthly" ? "90px" : "130px";

                            return (
                              <TableCell
                                key={`${emp.empid}-${dateKey}-${colIdx}`}
                                align="center"
                                sx={{
                                  position: "relative",
                                  height: 76,
                                  width: colWidth,
                                  minWidth: colWidth,
                                  maxWidth: colWidth,
                                  p: 0.8,
                                  borderRight: colIdx === activeColumns.length - 1 ? "none" : "1px solid #F1F5F9",
                                  borderBottom: "1px solid #F1F5F9",
                                  ...(isOutOfMonth
                                    ? {
                                      backgroundColor: "#F8FAFC",
                                      backgroundImage:
                                        "repeating-linear-gradient(45deg, #F8FAFC, #F8FAFC 8px, #F1F5F9 8px, #F1F5F9 16px)",
                                      opacity: 0.55,
                                    }
                                    : isCurrentDay
                                      ? {
                                        backgroundColor: "#F0F7FF",
                                        boxShadow: "inset 0 0 0 1.5px #BFDBFE",
                                      }
                                      : isWeekend
                                        ? { backgroundColor: "#FAFAFA" }
                                        : { backgroundColor: "#ffffff" }),
                                }}
                              >
                                {/* Date number at TOP-RIGHT of cell */}
                                <Typography
                                  sx={{
                                    position: "absolute",
                                    top: 5,
                                    right: 7,
                                    fontSize: 11,
                                    fontWeight: isCurrentDay ? 700 : 500,
                                    color: isOutOfMonth
                                      ? "#CBD5E1"
                                      : isCurrentDay
                                        ? "#2563EB"
                                        : "#94A3B8",
                                    lineHeight: 1,
                                    userSelect: "none",
                                  }}
                                >
                                  {dateNumber}
                                </Typography>

                                {/* Attendance status/events centered below date */}
                                <Box
                                  sx={{
                                    mt: 1.4,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: 28,
                                  }}
                                >
                                  {renderCellContent(
                                    emp.empid,
                                    date,
                                    isCurrentDay,
                                    isOutOfMonth
                                  )}
                                </Box>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </TableHead>


            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default EmployeeAttendance;